import {NamePath} from 'antd/lib/form/interface';
import {isEmpty, isObject, isString, mapKeys, mapValues, omit} from 'lodash';
import {FieldData} from 'rc-field-form/lib/interface';

import {CompanyKeyEnum} from '../../../../service/company-v2/company-const';
import {
    CompanyErrorType,
    CompanyFormErrorType,
    CompanyFormType,
    CompanyGeneralErrorType,
    CompanyInnerErrorType,
    CompanyListErrorType,
} from '../../../../service/company-v2/company-type';
import {CompanyNamesType} from '../../../../service/company-v2/types/company-names-type';
import {FormInstance} from '../../../../typings/antd';

import {convertErrorToFormError} from './error-helper';

type ExtraFieldsType =
    | CompanyKeyEnum.ExtraNames
    | CompanyKeyEnum.ExtraPhones
    | CompanyKeyEnum.ExtraEmails
    | CompanyKeyEnum.ExtraWebsites
    | CompanyKeyEnum.ExtraSocialNetworks
    | CompanyKeyEnum.ExtraDescriptions;

type ErrorValueType = CompanyListErrorType<CompanyGeneralErrorType> | CompanyGeneralErrorType; // == Exclude<CompanyErrorType[T], CompanyGeneralErrorType>

type SocialNetworksLinkErrorType = {
    socialNetworks?: Record<string, CompanyGeneralErrorType>;
    messengerPhones?: Record<string, CompanyGeneralErrorType>;
};

type SocialNetworksCatalogEntryType = [number, CompanyGeneralErrorType | Record<string, unknown>];

function transformSocialNetworksCatalogIdsToIndexes(
    form: FormInstance<CompanyFormType>,
    errorMessage: CompanyErrorType
): CompanyFormErrorType[CompanyKeyEnum.ExtraSocialNetworks] {
    const name = CompanyKeyEnum.ExtraSocialNetworks;
    const error = errorMessage[name];

    if (!isObject(error)) {
        return [];
    }

    if (Array.isArray(error)) {
        return error;
    }

    const fieldValue = form.getFieldValue(name);

    if (!Array.isArray(fieldValue)) {
        return [];
    }

    const entries = Object.entries(error).flatMap(
        ([catalogId, catalogError]: [
            string,
            SocialNetworksLinkErrorType | CompanyGeneralErrorType
        ]): Array<SocialNetworksCatalogEntryType> => {
            const index = fieldValue.findIndex((catalog) => catalog.catalogId.toString() === catalogId);

            if (index < 0) {
                return [];
            }

            if (Array.isArray(catalogError)) {
                return [[index, catalogError]];
            }

            const transformedValue: Record<string, unknown> = {};

            if (catalogError?.socialNetworks) {
                transformedValue.socialNetworks = catalogError.socialNetworks;
            }

            if (catalogError?.messengerPhones) {
                transformedValue.messengerPhones = catalogError.messengerPhones;
            }

            return [[index, transformedValue]];
        }
    );

    return Object.fromEntries(entries) as CompanyFormErrorType[CompanyKeyEnum.ExtraSocialNetworks];
}

// { "extraEmails": { "11": ["Ошибка google"]}, "50": ["Ошибка apple"]} } => { "extraEmails": { "0": ["Ошибка google"]}, "1": ["Ошибка apple"]} }
function transformCatalogIdsToIndexes<T extends ExtraFieldsType>(
    form: FormInstance<CompanyFormType>,
    errorMessage: CompanyErrorType,
    name: T,
    valueTransformer?: (value: CompanyErrorType[T]) => CompanyFormErrorType[T]
): CompanyFormErrorType[T] {
    const error = errorMessage[name];

    if (!isObject(error)) {
        return [];
    }

    if (Array.isArray(error)) {
        return error;
    }

    const entries = Object.entries(error)
        .map(([catalogId, value]: [string, ErrorValueType]) => {
            const fieldValue = form.getFieldValue(name as ExtraFieldsType);

            if (!Array.isArray(fieldValue)) {
                return null;
            }

            const index = fieldValue.findIndex((catalog) => catalog.catalogId.toString() === catalogId);

            if (index < 0) {
                return null;
            }

            const transformedValue = valueTransformer ? valueTransformer(value) : value;

            return [index, transformedValue];
        })
        .filter((value): value is [number, ErrorValueType] => value !== null);

    return Object.fromEntries(entries) as CompanyFormErrorType[T];
}

// The backend flattens the main names and every catalog override — its name and its Yandex short name —
// into one list and reports name errors by that list's index (CompanyUpsertSerializer._build_names).
// An empty name or short name is not sent, so it takes no slot. Rebuilding the same order from the form
// values is what lets a message land back on the field it came from instead of a non-existent `names.3`.
export function getFlatNamePaths(form: FormInstance<CompanyFormType>): Array<NamePath> {
    const names = form.getFieldValue(CompanyKeyEnum.Names);
    const extraNames = form.getFieldValue(CompanyKeyEnum.ExtraNames);

    const mainNamePaths: Array<NamePath> = Array.isArray(names)
        ? names.map((_name, index: number) => [CompanyKeyEnum.Names, index, 'name'])
        : [];

    const catalogNamePaths: Array<NamePath> = Array.isArray(extraNames)
        ? extraNames.flatMap((catalog: CompanyFormType[CompanyKeyEnum.ExtraNames][number], catalogIndex: number) =>
              (catalog?.names ?? []).flatMap((name, index: number) => [
                  ...(name?.name?.trim()
                      ? [[CompanyKeyEnum.ExtraNames, catalogIndex, 'names', index, 'name'] as NamePath]
                      : []),
                  ...(name?.shortName?.trim()
                      ? [[CompanyKeyEnum.ExtraNames, catalogIndex, 'names', index, 'shortName'] as NamePath]
                      : []),
              ])
          )
        : [];

    return [...mainNamePaths, ...catalogNamePaths];
}

type NameErrorsType = {
    fieldErrors: Array<FieldData>;
    // What the flat list did not explain: an index out of its range, or a message of a shape the form
    // cannot show on a field. It stays in `names` so the generic conversion still surfaces it —
    // an unmatched error must not disappear, otherwise a company just fails to save with no reason shown.
    restNames: CompanyErrorType['names'];
};

export function getNameFieldErrors(
    form: FormInstance<CompanyFormType>,
    errorMessage: CompanyErrorType
): NameErrorsType {
    const error = errorMessage.names;

    if (!isObject(error) || Array.isArray(error)) {
        return {fieldErrors: [], restNames: error};
    }

    const paths = getFlatNamePaths(form);
    const fieldErrors: Array<FieldData> = [];
    const restNames: Record<number, CompanyInnerErrorType<CompanyNamesType['names'][0]>> = {};

    Object.entries(error).forEach(([index, value]) => {
        const name = paths[Number(index)];
        const errors = Array.isArray(value) ? value : value?.name;

        if (!name || !Array.isArray(errors) || !errors.every(isString)) {
            restNames[Number(index)] = value;

            return;
        }

        fieldErrors.push({name, errors});
    });

    // eslint-disable-next-line no-undefined
    return {fieldErrors, restNames: isEmpty(restNames) ? undefined : restNames};
}

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity, max-statements
export function getFormErrors(
    form: FormInstance<CompanyFormType>,
    errorMessage: CompanyErrorType
): Array<FieldData> | null {
    const {fieldErrors: nameErrors, restNames} = getNameFieldErrors(form, errorMessage);
    const fixedErrorMessage = omit(
        {...errorMessage, names: restNames} as unknown as CompanyFormErrorType,
        restNames ? [] : ['names']
    );

    if (fixedErrorMessage.extraNames && !Array.isArray(fixedErrorMessage.extraNames)) {
        fixedErrorMessage.extraNames = transformCatalogIdsToIndexes(
            form,
            errorMessage,
            CompanyKeyEnum.ExtraNames,
            (value) => ({names: value})
        );
    }

    if (fixedErrorMessage.searchNames && !Array.isArray(fixedErrorMessage.searchNames)) {
        fixedErrorMessage.searchNames = [...new Set(Object.values(fixedErrorMessage.searchNames).flat())];
    }

    if (fixedErrorMessage.address && !Array.isArray(fixedErrorMessage.address)) {
        fixedErrorMessage.address = {
            ...errorMessage.address,
            country: errorMessage.address?.countryId,
            region: errorMessage.address?.regionId,
            city: errorMessage.address?.cityId,
            latLng: [...new Set([...(errorMessage.address?.lat || []), ...(errorMessage.address?.lon || [])])],
        };
    }

    if (fixedErrorMessage.legal && !Array.isArray(fixedErrorMessage.legal) && errorMessage.legal?.formId) {
        fixedErrorMessage.legal.form = errorMessage.legal?.formId;
    }

    if (fixedErrorMessage.rocketdataCategories && !Array.isArray(fixedErrorMessage.rocketdataCategories)) {
        fixedErrorMessage.rocketdataCategories = [
            ...new Set(Object.values(fixedErrorMessage.rocketdataCategories).flat()),
        ];
    }

    if (fixedErrorMessage.phones && !Array.isArray(errorMessage.phones)) {
        fixedErrorMessage.phones = mapValues(errorMessage.phones, (error) => ({phone: error}));
    }

    if (fixedErrorMessage.extraPhones && !Array.isArray(fixedErrorMessage.extraPhones)) {
        fixedErrorMessage.extraPhones = transformCatalogIdsToIndexes(
            form,
            errorMessage,
            CompanyKeyEnum.ExtraPhones,
            (phones) => ({phones: Array.isArray(phones) ? phones : mapValues(phones, (error) => ({phone: error}))})
        );
    }

    if (fixedErrorMessage.extraEmails && !Array.isArray(fixedErrorMessage.extraEmails)) {
        fixedErrorMessage.extraEmails = transformCatalogIdsToIndexes(
            form,
            errorMessage,
            CompanyKeyEnum.ExtraEmails,
            (value) => ({email: value})
        );
    }

    if (fixedErrorMessage.extraWebsites && !Array.isArray(fixedErrorMessage.extraWebsites)) {
        fixedErrorMessage.extraWebsites = transformCatalogIdsToIndexes(
            form,
            errorMessage,
            CompanyKeyEnum.ExtraWebsites,
            (value) => ({websites: value})
        );
    }

    if (fixedErrorMessage.extraSocialNetworks && !Array.isArray(fixedErrorMessage.extraSocialNetworks)) {
        fixedErrorMessage.extraSocialNetworks = transformSocialNetworksCatalogIdsToIndexes(form, errorMessage);
    }

    if (fixedErrorMessage.workingHours && !Array.isArray(fixedErrorMessage.workingHours)) {
        fixedErrorMessage.workingHours = undefined; // eslint-disable-line no-undefined
    }

    if (fixedErrorMessage.specialHours && !Array.isArray(fixedErrorMessage.specialHours)) {
        fixedErrorMessage.specialHours = undefined; // eslint-disable-line no-undefined
    }

    if (fixedErrorMessage.extraWorkingHours && !Array.isArray(fixedErrorMessage.extraWorkingHours)) {
        fixedErrorMessage.extraWorkingHours = undefined; // eslint-disable-line no-undefined
    }

    if (fixedErrorMessage.extraDescriptions && !Array.isArray(fixedErrorMessage.extraDescriptions)) {
        fixedErrorMessage.extraDescriptions = transformCatalogIdsToIndexes(
            form,
            errorMessage,
            CompanyKeyEnum.ExtraDescriptions
        );
    }

    if (fixedErrorMessage.attributesGoogle) {
        fixedErrorMessage.attributes = {
            ...errorMessage.attributes,
            attributesGoogle: errorMessage?.attributesGoogle,
        };
    }

    if (fixedErrorMessage.attributesYandex) {
        fixedErrorMessage.attributes = {
            ...errorMessage.attributes,
            attributesYandex: errorMessage?.attributesYandex,
        };
    }

    if (fixedErrorMessage.attributes && !Array.isArray(fixedErrorMessage.attributes)) {
        const attributes = form.getFieldValue(CompanyKeyEnum.Attributes);

        fixedErrorMessage.attributes = {
            ...fixedErrorMessage.attributes,
            attributesGoogle:
                fixedErrorMessage?.attributesGoogle ??
                (Array.isArray(fixedErrorMessage.attributes?.attributesGoogle)
                    ? fixedErrorMessage.attributes?.attributesGoogle
                    : mapKeys(fixedErrorMessage.attributes?.attributesGoogle, (_value, key) =>
                          attributes.attributesGoogle
                              .flatMap((attribute) => ('items' in attribute ? attribute.items : []))
                              .findIndex((attribute) =>
                                  attribute.values.some(({templateId}) => templateId === Number(key))
                              )
                      )),
            attributesYandex: Array.isArray(fixedErrorMessage.attributes?.attributesYandex)
                ? fixedErrorMessage.attributes?.attributesYandex
                : mapKeys(fixedErrorMessage.attributes?.attributesYandex, (_value, key) =>
                      attributes.attributesYandex.findIndex((attribute) =>
                          attribute.values.some(({templateId}) => templateId === Number(key))
                      )
                  ),
        };
    }

    return [...convertErrorToFormError(fixedErrorMessage), ...nameErrors];
}

export function removeErrorIfExists(name: NamePath, form: FormInstance<CompanyFormType>): void {
    const error = form.getFieldError(name);

    if (!Array.isArray(error)) {
        return;
    }

    if (error.length > 0) {
        form.setFields([{name, errors: []}]);
    }

    if (!Array.isArray(name)) {
        return;
    }

    // We might have an error set on widget root. For example: {workingHours: ['common error']}
    // and user changed some deep property inside workingHours like a break time
    const possibleNames = name.map((_path, index, array) => array.slice(0, index + 1));

    possibleNames.forEach((possibleName) => {
        const possibleError = form.getFieldError(possibleName);

        if (Array.isArray(possibleError) && possibleError.length > 0) {
            form.setFields([{name: possibleName, errors: []}]);
        }
    });
}
